import { _js } from '@ifixit/utils/localize';
import { Alert, AlertDescription, AlertTitle } from '@ifixit/ui/components/alert';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Input } from '@ifixit/ui/components/input';
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from '@ifixit/ui/components/select';
import { useState } from 'react';
import { apiClient } from 'Shared/api_client';
import { apiErrorMessage } from 'Shared/editor/api_error_message';
import { PrimaryButton } from 'Shared/editor/buttons';
import { EditorFooter } from 'Shared/editor/editor_footer';
import type { EditorVersionLinkProps } from 'Shared/editor/editor_version_link';
import { SectionCard } from 'Shared/editor/section_card';
import { suggestTopics } from 'Shared/editor/suggest_api';
import { SuggestInput } from 'Shared/editor/SuggestInput';
import { Field } from 'Shared/editor/field';
import { requireAuth } from 'Shared/require-auth';
import { generateGuideTitle, nextAutoTitle, type TitleAutogen, titleIsAuto } from './guide_title';
import { useTopicExists } from './use_guide_edit_api';
import { SentryError } from '@ifixit/sentry';

/** Props bootstrapped by GuideEditLib::displayNewV2(). */
type GuideNewV2Props = {
   /** Device name from the /Guide/new wizard's pick; '' when starting cold. */
   device: string;
   types: {
      value: string;
      label: string;
      description: string | null;
      /** Whether the type demands a part/thing ("What are you replacing?"). */
      thingRequired: boolean;
      /** The type's part prompt; null for types without one (teardowns...). */
      thingPrompt: string | null;
   }[];
   defaultType: string;
   /** The view language; sent so the guide is created in the locale being
    * viewed, not the site default. */
   langid: string;
   titleAutogen: TitleAutogen;
   editorVersionLink: EditorVersionLinkProps | null;
   showLicenseDisclaimer: boolean;
};

/**
 * The v2 new-guide page: just the identity fields. Creating POSTs the v2
 * guides API (which auto-titles when the title is left blank) and forwards
 * to the v2 editor for everything else — intro text, steps, and details are
 * edits on the created guide, not part of the form.
 */
export function NewGuidePage(props: GuideNewV2Props) {
   const [device, setDevice] = useState(props.device);
   const [type, setType] = useState(props.defaultType);
   const [subject, setSubject] = useState('');
   const [title, setTitle] = useState(() =>
      props.titleAutogen.enabled
         ? generateGuideTitle(props.titleAutogen, {
              type: props.defaultType,
              device: props.device,
              subject: '',
           })
         : ''
   );
   const [isCreating, setIsCreating] = useState(false);
   const [error, setError] = useState<Error | null>(null);

   // v1's auto-title: the title follows the identity fields until the user
   // writes their own (or auto-titling is off for the site/language). An
   // emptied title resumes tracking on the next field change.
   const retitle = (next: { type?: string; device?: string; subject?: string }) => {
      const fields = { type, device, subject, ...next };
      if (
         props.titleAutogen.enabled &&
         titleIsAuto(props.titleAutogen, { title, type, device, subject })
      ) {
         setTitle(nextAutoTitle(props.titleAutogen, title, fields));
      }
   };

   // The "creates a new category" hint, keyed off the committed (blurred)
   // device like the Intro tab's device field.
   const [settledDevice, setSettledDevice] = useState(props.device);
   const deviceExists = useTopicExists(settledDevice);
   const deviceUnmatched = settledDevice.trim() !== '' && deviceExists.data === false;

   const selectedType = props.types.find(candidate => candidate.value === type);
   const subjectMissing = Boolean(selectedType?.thingRequired) && subject.trim() === '';
   const canCreate = device.trim() !== '' && type !== '' && !subjectMissing && !isCreating;

   const create = async () => {
      setIsCreating(true);
      setError(null);
      try {
         const body: Record<string, string> = {
            category: device.trim(),
            type,
            langid: props.langid,
         };
         if (subject.trim() !== '') {
            body.subject = subject.trim();
         }
         if (title.trim() !== '') {
            body.title = title.trim();
         }
         const response = await apiClient.post('guides', body);
         const created = response.data as { guideid: number };
         // The editor takes it from here; the flag keeps /Guide/edit on v2.
         window.location.assign(`/Guide/edit/${created.guideid}`);
      } catch (requestError) {
         setError(
            requestError instanceof Error ? requestError : new SentryError(String(requestError))
         );
         setIsCreating(false);
      }
   };

   const handleCreate = () => {
      requireAuth({
         message: _js('Log in to create a guide.'),
         onAuthorize: () => void create(),
      });
   };

   return (
      <div className="mx-auto w-full max-w-5xl pb-8 md:px-8">
         <h2 className="my-6 text-lg font-semibold text-gray-950 max-md:px-4">
            {_js('Create a new guide')}
         </h2>

         {error != null && (
            <Alert variant="critical" className="mb-4">
               <AlertTitle>{_js('The guide was not created.')}</AlertTitle>
               <AlertDescription>
                  {apiErrorMessage(error, { login: _js('Log in to create a guide.') })}
               </AlertDescription>
            </Alert>
         )}

         <SectionCard className="[&_input]:max-w-none [&_button]:max-w-none">
            <div className="flex flex-col gap-6">
               <Field label={_js('Device')} hint={_js('What device are you working on?')}>
                  <SuggestInput
                     queryKey="guide-topic-suggest"
                     className="w-full"
                     value={device}
                     onValueChange={value => {
                        retitle({ device: value });
                        setDevice(value);
                     }}
                     onSelect={suggestion => {
                        retitle({ device: suggestion.label });
                        setDevice(suggestion.label);
                        setSettledDevice(suggestion.label);
                     }}
                     onBlur={() => setSettledDevice(device)}
                     fetchSuggestions={suggestTopics}
                  />
                  {deviceUnmatched && (
                     <span className="mt-1 flex items-center gap-1.5 text-xs text-amber-600">
                        <FaIcon name="triangle-exclamation" className="size-3.5" aria-hidden />
                        {_js(
                           "This device isn't an existing category. Creating the guide creates it."
                        )}
                     </span>
                  )}
               </Field>

               <Field
                  label={_js('What type of guide is this?')}
                  hint={
                     selectedType?.description ?? _js('The kind of procedure this guide explains.')
                  }
               >
                  <Select
                     value={type}
                     onValueChange={value => {
                        retitle({ type: value });
                        setType(value);
                     }}
                  >
                     <SelectTrigger className="w-full">
                        <SelectValue placeholder={_js('Choose a type')} />
                     </SelectTrigger>
                     <SelectContent>
                        {props.types.map(option => (
                           <SelectItem key={option.value} value={option.value}>
                              {option.label}
                           </SelectItem>
                        ))}
                     </SelectContent>
                  </Select>
               </Field>

               {selectedType?.thingPrompt != null && (
                  <Field
                     label={selectedType.thingPrompt}
                     hint={
                        selectedType.thingRequired
                           ? _js("Name the specific part or component, like 'Battery.'")
                           : _js('Optional. The part or component the guide works on.')
                     }
                  >
                     <Input
                        value={subject}
                        onChange={event => {
                           retitle({ subject: event.target.value });
                           setSubject(event.target.value);
                        }}
                     />
                  </Field>
               )}

               <Field
                  label={_js('Title')}
                  hint={_js('Optional. Left blank, the title comes from the fields above.')}
               >
                  <Input value={title} onChange={event => setTitle(event.target.value)} />
               </Field>

               <div className="flex justify-end">
                  <PrimaryButton
                     disabled={!canCreate}
                     isPending={isCreating}
                     onClick={handleCreate}
                  >
                     {isCreating ? _js('Creating...') : _js('Create guide')}
                  </PrimaryButton>
               </div>
            </div>
         </SectionCard>

         <EditorFooter
            editorVersionLink={props.editorVersionLink}
            showLicenseDisclaimer={props.showLicenseDisclaimer}
            className="max-md:px-4"
         />
      </div>
   );
}
